//
/* GS://PROTOCOLO
 * Arquivo: src/services/bathService.js
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 1.2.2 – FIX: Fechamento Automático de Lote
 * Impacto da Versão: [src/services/bathService.js]
 * Data: 26/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [NOVO]: Implementada verificação de encerramento do Lote após aplicação do Banho.
 * - LÓGICA: Após processar as peças, verifica se restam itens 'BRUTA' no lote.
 * - AÇÃO: Se contagem == 0, atualiza `statusLot` para 'FINALIZADO'.
 */

import { db } from '../firebase/config';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';

import productService from './productService';
import {
  GSM_COLLECTIONS,
  INVENTORY_MOVEMENT_TYPES,
} from './gs_nf_module_dataModel';

// Códigos de status aceitos como "BRUTA" para a FASE 5
const BRUTA_STATUSES = ['BRUTA', 'BRUTO', 'Aguardando Destinação'];

/**
 * Normaliza número seguro (evita NaN).
 */
function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Normaliza o lote retornado do Firestore, garantindo campos básicos.
 */
function mapLotDoc(lotSnap) {
  if (!lotSnap.exists()) return null;
  const data = lotSnap.data() || {};

  return {
    id: lotSnap.id,
    ...data,
  };
}

/**
 * Normaliza itens de lote, focando em campos usados na FASE 5.
 */
function mapLotItemDoc(itemSnap) {
  const data = itemSnap.data() || {};
  return {
    id: itemSnap.id,
    ref: itemSnap.ref,
    sku: data.sku || data.code || null,
    description:
      data.description ||
      data.name ||
      data.descriptionNF ||
      data.supplierItemName ||
      null,
    statusPiece: data.statusPiece || null,
    quantity: typeof data.quantity === 'number' ? data.quantity : 0,
    weightGrams: typeof data.weightGrams === 'number' ? data.weightGrams : 0,
    costBruto: typeof data.costBruto === 'number' ? data.costBruto : 0,
    categoryId: data.categoryId || null,
    stockRef: data.stockRef || null,
  };
}

/**
 * Consolida resultado do Banho em:
 * - products (produto técnico),
 * - inventory_movements (BANHO_ENTRADA),
 * - stockRef em lots/{lotId}/items/{itemId}.
 */
async function consolidateBathResultsToProductsAndInventory(
  lotContext,
  itemsResults
) {
  const {
    lotId,
    lotCode,
    nfId,
    nfNumber,
    bathPricePerGram,
    createdByUserId,
  } = lotContext || {};

  if (!Array.isArray(itemsResults) || itemsResults.length === 0) {
    return;
  }

  const inventoryBatch = writeBatch(db);
  const lotItemsBatch = writeBatch(db);

  let hasInventoryOps = false;
  let hasLotItemOps = false;

  for (const item of itemsResults) {
    const {
      itemDocRef,
      itemId,
      quantity,
      weightGrams,
      costBruto,
      costFinal,
      description,
      stockRef,
      categoryId,
      supplierId,
    } = item;

    const qty = toNumber(quantity);
    if (!qty || qty <= 0) continue;

    // Consolida produto técnico em `products`
    const upsertResult = await productService.upsertTechnicalProductFromLotItem({
      lotId,
      lotCode,
      nfId: nfId || null,
      nfNumber: nfNumber || null,
      categoryId: categoryId || null,
      supplierId: supplierId || null,
      lotItemId: itemId,
      description,
      quantity: qty,
      costBruto: toNumber(costBruto),
      costFinal: toNumber(costFinal),
      weightGrams: toNumber(weightGrams),
      bathPricePerGram: toNumber(bathPricePerGram),
      existingProductId: stockRef || null,
    });

    if (!upsertResult) {
      continue;
    }

    const {
      productId,
      sku,
      quantityBefore,
      quantityAfter,
    } = upsertResult;

    // Atualiza stockRef no item do lote, se necessário
    if (!stockRef || stockRef !== productId) {
      lotItemsBatch.update(itemDocRef, {
        stockRef: productId,
      });
      hasLotItemOps = true;
    }

    // Movimento de estoque: BANHO_ENTRADA
    const invRef = doc(collection(db, GSM_COLLECTIONS.INVENTORY_MOVEMENTS));
    const qtyBefore = toNumber(quantityBefore);
    const qtyAfter = toNumber(quantityAfter);
    const quantityDelta = qtyAfter - qtyBefore;

    inventoryBatch.set(invRef, {
      productId,
      sku: sku || '',
      movementType: INVENTORY_MOVEMENT_TYPES.BANHO_ENTRADA,
      quantityBefore: qtyBefore,
      quantityAfter: qtyAfter,
      quantityDelta,
      nfId: nfId || null,
      nfItemId: null, // pode ser ligado no futuro ao NF_ITEM, se necessário
      lotId,
      bathOperationId: null, // pode ser refinado para apontar para bathHistory específico
      assemblyId: null,
      saleId: null,
      createdAt: serverTimestamp(),
      createdByUserId: createdByUserId || null,
      notes: `Entrada de estoque técnico pós-banho. Lote ${lotCode || lotId || ''} – NF ${nfNumber || ''}.`,
    });
    hasInventoryOps = true;
  }

  const promises = [];
  if (hasInventoryOps) promises.push(inventoryBatch.commit());
  if (hasLotItemOps) promises.push(lotItemsBatch.commit());

  if (promises.length > 0) {
    await Promise.all(promises);
  }
}

/**
 * Serviço da FASE 5 — Banho.
 */
const bathService = {
  /**
   * getLotBathData
   *
   * Carrega:
   * - Dados do lote (lots/{lotId}).
   * - Itens associados ao lote considerados BRUTOS (subcoleção `items`).
   *
   * Retorno:
   * { lot, items }
   */
  async getLotBathData(lotId) {
    if (!lotId) {
      throw new Error('[bathService.getLotBathData] lotId não informado.');
    }

    // Carrega lote
    const lotRef = doc(db, 'lots', lotId);
    const lotSnap = await getDoc(lotRef);
    const lot = mapLotDoc(lotSnap);

    if (!lot) {
      throw new Error('[bathService.getLotBathData] Lote não encontrado.');
    }

    // Carrega itens BRUTOS do lote
    const itemsRef = collection(lotRef, 'items');
    const itemsQuery = query(
      itemsRef,
      where('statusPiece', 'in', BRUTA_STATUSES)
    );
    const itemsSnap = await getDocs(itemsQuery);

    const items = [];
    itemsSnap.forEach((docSnap) => {
      items.push(mapLotItemDoc(docSnap));
    });

    return { lot, items };
  },

  /**
   * applyBathToLot
   *
   * Aplica a FASE 5 — Banho para todas as peças brutas de um lote.
   *
   * Parâmetros esperados:
   * {
   * lotId,
   * lotCode,
   * nfNumber,
   * bathPricePerGram,  // número (R$ por grama)
   * extraCost,         // número (R$ custo fixo extra) – não entra na fórmula da peça
   * serviceProvider    // string ou null
   * }
   *
   * Observação:
   * - Esta função agora também dispara a consolidação em `products` + movimentos de estoque.
   */
  async applyBathToLot({
    lotId,
    lotCode,
    nfNumber,
    bathPricePerGram,
    extraCost = 0,
    serviceProvider = null,
  }) {
    if (!lotId) {
      throw new Error('[bathService.applyBathToLot] lotId não informado.');
    }
    if (
      !bathPricePerGram ||
      typeof bathPricePerGram !== 'number' ||
      bathPricePerGram <= 0
    ) {
      throw new Error(
        '[bathService.applyBathToLot] Valor da grama do banho inválido.'
      );
    }
    if (typeof extraCost !== 'number' || extraCost < 0) {
      throw new Error(
        '[bathService.applyBathToLot] Custo extra inválido.'
      );
    }

    const lotRef = doc(db, 'lots', lotId);
    const lotSnap = await getDoc(lotRef);
    if (!lotSnap.exists()) {
      throw new Error('[bathService.applyBathToLot] Lote não encontrado.');
    }

    const lotData = lotSnap.data() || {};

    // Carrega itens BRUTOS do lote novamente no servidor antes de aplicar o Banho
    const itemsRef = collection(lotRef, 'items');
    const itemsQuery = query(
      itemsRef,
      where('statusPiece', 'in', BRUTA_STATUSES)
    );
    const itemsSnap = await getDocs(itemsQuery);

    if (itemsSnap.empty) {
      throw new Error(
        '[bathService.applyBathToLot] Nenhuma peça BRUTA encontrada para este lote.'
      );
    }

    const batch = writeBatch(db);

    // Totais para histórico
    let totalItems = 0;
    let totalWeight = 0;
    let totalBrutoBefore = 0;
    let totalFinalAfter = 0;

    // Itens a consolidar em products + inventory_movements
    const itemsForConsolidation = [];

    itemsSnap.forEach((itemDoc) => {
      const itemData = itemDoc.data() || {};

      const quantity = typeof itemData.quantity === 'number'
        ? itemData.quantity
        : 0;
      const weightGrams = typeof itemData.weightGrams === 'number'
        ? itemData.weightGrams
        : 0;
      const costBruto = typeof itemData.costBruto === 'number'
        ? itemData.costBruto
        : 0;

      // Segurança: peso é obrigatório para FASE 5
      if (!weightGrams || weightGrams <= 0) {
        throw new Error(
          `[bathService.applyBathToLot] Peso não informado ou inválido para o item ${itemDoc.id}.`
        );
      }

      // Fórmula oficial confirmada por Dario:
      // custo_final = (peso_da_peça * valor_grama_banho) + custo_bruto
      const costFinal = (weightGrams * bathPricePerGram) + costBruto;

      // Totais para histórico (considerando quantidade)
      const qty = quantity || 1;
      totalItems += quantity;
      totalWeight += weightGrams * qty;
      totalBrutoBefore += costBruto * qty;
      totalFinalAfter += costFinal * qty;

      // Atualiza peça BRUTA → PRONTA
      batch.update(itemDoc.ref, {
        statusPiece: 'PRONTA',
        costFinal,
        lastBathAt: serverTimestamp(),
        lastBathPricePerGram: bathPricePerGram,
        lastBathExtraCost: extraCost,
        lastBathServiceProvider: serviceProvider,
      });

      const mappedItem = mapLotItemDoc(itemDoc);
      itemsForConsolidation.push({
        itemDocRef: itemDoc.ref,
        itemId: itemDoc.id,
        quantity,
        weightGrams,
        costBruto,
        costFinal,
        description: mappedItem.description,
        stockRef: mappedItem.stockRef,
        categoryId: mappedItem.categoryId,
        supplierId: lotData.supplierId || null,
      });
    });

    // Registro de histórico de Banho vinculado ao lote
    const bathHistoryRef = collection(lotRef, 'bathHistory');
    const bathHistoryDocRef = doc(bathHistoryRef);

    batch.set(bathHistoryDocRef, {
      type: 'BANHO',
      createdAt: serverTimestamp(),
      lotId,
      lotCode: lotCode || lotData.lotCode || null,
      nfNumber: nfNumber || lotData.nfNumber || null,
      previousLotStatus: lotData.statusLot || lotData.status || null,
      params: {
        bathPricePerGram,
        extraCost,
        serviceProvider,
      },
      totals: {
        totalItems,
        totalWeightGrams: totalWeight,
        totalBrutoBefore,
        totalFinalAfter,
      },
    });

    // Importante:
    // O batch inicial trata das atualizações internas (Itens + Histórico).
    await batch.commit();

    // Consolidação em products + inventory_movements
    await consolidateBathResultsToProductsAndInventory(
      {
        lotId,
        lotCode: lotCode || lotData.lotCode || lotData.lotNumber || null,
        nfId: lotData.nfId || null,
        nfNumber: nfNumber || lotData.nfNumber || null,
        bathPricePerGram,
        createdByUserId: lotData.createdByUserId || null,
      },
      itemsForConsolidation
    );

    // [GS-NOVO] Verificação de Fechamento do Lote
    // Consulta se ainda existem itens BRUTA no lote. Se não, encerra o Lote.
    const remainingQuery = query(
      collection(db, 'lots', lotId, 'items'),
      where('statusPiece', 'in', BRUTA_STATUSES)
    );
    const remainingSnap = await getDocs(remainingQuery);

    if (remainingSnap.empty) {
      const closingBatch = writeBatch(db);
      closingBatch.update(lotRef, {
        statusLot: 'FINALIZADO',
        status: 'FINALIZADO',
        updatedAt: serverTimestamp(),
      });
      await closingBatch.commit();
    }
  },
};

export default bathService;